/**
 * Who may use which part of Settings — mirror of backend/utils/settingsAccess.js
 * (keep the two identical; the API enforces the same matrix).
 *
 *   ownerTools        Submissions and Regions — the company's OW/sOW/OWp/RM
 *   ownerConsole      Owner Console (website, hiring, affiliates, referrals, notifications) —
 *                     the company's owners (OW/sOW, or OWp for a partner company);
 *                     the API also asks for a fresh password unlock
 *   orgOwner          The same owners, for the setup wizard (no unlock on top)
 *   platformAdmin     White Knight's own OW/sOW only: partner companies, integration partners
 *   orgAdmin          Terms, Drivers, Users, Fleet, Background Checks, Insurance —
 *                     'write' for management admin-and-up and provider admin-and-up,
 *                     'read' for provider answering service, 'none' otherwise
 *   sitesRates        'write' management RM/OW/sOW/OWp; 'read' provider admin-and-up, answering service,
 *                     management admin/dispatcher; 'none' otherwise
 *   dispatchSettings  Dispatch radius + dispatch/ETA timing on the Dashboard tab — management admin-and-up
 *
 * Every company (White Knight and each partner) has the same matrix inside its own
 * organization; `user.orgKind` ('platform' | 'partner') comes from the login payload.
 */
const OWNER_ROLES = ['OW', 'sOW', 'OWp'];
const PLATFORM_OWNER_ROLES = ['OW', 'sOW'];

const secondaryOf = (user) => {
  const raw = user.secondaryRoles;
  if (Array.isArray(raw)) return Object.fromEntries(raw.map((r) => [r, true]));
  return raw || {};
};

export function settingsAccess(user, org) {
  const none = {
    ownerTools: false, ownerConsole: false, orgOwner: false, platformAdmin: false,
    orgAdmin: 'none', sitesRates: 'none', dispatchSettings: false, driverOnly: false, isOrg2: false, orgKind: 'platform'
  };
  if (!user) return none;
  const role = user.primaryRole || user.role || 'N/A';
  const org2 = Number(user.organizationLevel) === 2 || role === 'SP';
  const sec = secondaryOf(user);
  const isOwner = OWNER_ROLES.includes(role);
  const orgKind = (org && org.kind) || user.orgKind || 'platform';
  const org1RmUp = !org2 && (isOwner || role === 'RM');
  const org1AdminUp = org1RmUp || (!org2 && sec.admin === true);
  const org2AdminUp = org2 && (role === 'SP' || sec.admin === true);
  const org2Answering = org2 && !org2AdminUp && sec.answeringService === true;
  const driverOnly = !org1AdminUp && !org2AdminUp && sec.driver === true && !sec.dispatcher && !sec.answeringService;

  let sitesRates = 'none';
  if (org1RmUp) sitesRates = 'write';
  else if (org2AdminUp || sec.answeringService === true || (!org2 && (sec.admin === true || sec.dispatcher === true))) sitesRates = 'read';

  let orgAdmin = 'none';
  if (org1AdminUp || org2AdminUp) orgAdmin = 'write';
  else if (org2Answering) orgAdmin = 'read';

  const orgOwner = !org2 && isOwner;
  const platformAdmin = !org2 && PLATFORM_OWNER_ROLES.includes(role) && orgKind === 'platform';
  return {
    ownerTools: org1RmUp, ownerConsole: orgOwner, orgOwner, platformAdmin,
    orgAdmin, sitesRates, dispatchSettings: org1AdminUp, driverOnly, isOrg2: org2, orgKind
  };
}

export { OWNER_ROLES, PLATFORM_OWNER_ROLES };
export default settingsAccess;
